import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Contact {
  id: string
  full_name: string
  email: string
  title: string
  company_name: string
  status: string
  last_contact: string
}

interface Deal {
  id: string
  name: string
  contact_name: string
  company_name: string
  stage: string
  value: number
  probability: number
  owner: string
}

export default function CRMPage() {
  const [contacts, setContacts] = useState<Contact[]>([])
  const [deals, setDeals] = useState<Deal[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [contactsRes, dealsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/crm/contacts'),
      fetch(API_BASE + '/crm/deals'),
      fetch(API_BASE + '/crm/statistics')
    ])
    if (contactsRes.ok) setContacts((await contactsRes.json()).contacts || [])
    if (dealsRes.ok) setDeals((await dealsRes.json()).deals || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'closed_won': return 'bg-green-100 text-green-800'
      case 'closed_lost': return 'bg-red-100 text-red-800'
      case 'negotiation': return 'bg-purple-100 text-purple-800'
      case 'proposal': return 'bg-blue-100 text-blue-800'
      case 'needs_analysis': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'customer': return 'bg-green-100 text-green-800'
      case 'prospect': return 'bg-blue-100 text-blue-800'
      case 'lead': return 'bg-yellow-100 text-yellow-800'
      case 'churned': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">CRM</h1>
          <div className="space-x-2">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Contact</button>
            <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">+ New Deal</button>
          </div>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Contacts</div>
              <div className="text-2xl font-bold">{statistics.total_contacts}</div>
              <div className="text-xs text-green-600">{statistics.customers} customers</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Open Deals</div>
              <div className="text-2xl font-bold">{statistics.open_deals}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pipeline Value</div>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(statistics.pipeline_value)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Win Rate</div>
              <div className="text-2xl font-bold text-green-600">{statistics.win_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Revenue MTD</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.revenue_mtd)}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Contacts</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {contacts.slice(0, 8).map(contact => (
                  <tr key={contact.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{contact.full_name}</div>
                      <div className="text-xs text-gray-500">{contact.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{contact.company_name}</div>
                      <div className="text-xs text-gray-500">{contact.title}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(contact.status)}`}>{contact.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Sales Pipeline</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deal</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {deals.slice(0, 8).map(deal => (
                  <tr key={deal.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-sm">{deal.name}</div>
                      <div className="text-xs text-gray-500">{deal.contact_name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-green-600">{formatCurrency(deal.value)}</div>
                      <div className="text-xs text-gray-500">{deal.probability}% prob.</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStageColor(deal.stage)}`}>
                        {deal.stage.replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
